/* ---------- CP-002's earthing busbar -PE to -WE1 (#1907, row 17; engineer 3, 6186890646) ----------
 * IEC 81346-2 gives the earthing busbar the class WE, so the bar's tag "PE" is "WE1". A save made with the old tag names it as a placed
 * device ({ tag: "PE" }) and in every conductor end on it ("PE:3"), and in any reading taken there. The conductors' own mark "PE" (a
 * protective conductor's identifier, IEC 60445) and the terminal marks "XD0:PE", "XD1:GP41-PE", "TB1:PE" stay: they are not the bar.
 * The save migration in packages/progress uses this (format 15); nothing else does. Self-contained on purpose: the migration imports
 * this file alone, not CP-002's pack. */

/** The bar's old tag and its new one (without the leading dash). */
export const CP002_TAG_RENAME = { from: "PE", to: "WE1" } as const;

/** A terminal on the bar, wherever it stands as a whole terminal: "PE:3", or inside a key ("PE:3|XD2:6"); never "XD0:PE" or "GP41-PE". */
const ON_BAR = /(?<![\w:-])PE(?=:\d)/g;
const renamed = (s: string) => s.replace(ON_BAR, CP002_TAG_RENAME.to);

function walk(v: any, key?: string): any {
  if (typeof v === "string") return key === "tag" && v === CP002_TAG_RENAME.from ? CP002_TAG_RENAME.to : renamed(v);
  if (Array.isArray(v)) return v.map(x => walk(x));
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [renamed(k), walk(x, k)]));
  return v;
}

const one = <A extends { model?: any; ui?: any }>(s: A): A => {
  const asText = typeof s.model === "string", model = walk(asText ? JSON.parse(s.model) : s.model ?? null);
  return { ...s, model: asText ? JSON.stringify(model) : model, ui: walk(s.ui ?? {}) };
};
/** A saved CP-002 project ({ model, ui }, the model as a JSON string or an object, as saved) with the bar's new tag: its own work and
 *  each earlier attempt it keeps ("Start again", #1220). */
export function renameCp002Save<T extends { model?: any; ui?: any; attempts?: any[] }>(s: T): T {
  const out = one(s);
  return Array.isArray(s.attempts) ? { ...out, attempts: s.attempts.map(a => one(a)) } : out;
}

/* ---------- CP-002's distribution block -XC1 to -XD4 (#1907, row 17; the owner's #1886 decision for BS-001's block) ----------
 * XC is not an IEC 81346-2 class for it: a terminal-type connecting object for low-voltage power is class XD, and -XD4 is the next free
 * number after -XD0 to -XD3. A save made with the old tag names it as a placed device ({ tag: "XC1" }), in every conductor end on it
 * ("XC1:L1", "XC1:L2.1"), and in any key or reading that holds one. CP-002 has no other tag that starts XC1. The save migration in
 * packages/progress uses this (format 17). */

/** The block's old tag and its new one (without the leading dash). */
export const CP002_BLOCK_RENAME = { from: "XC1", to: "XD4" } as const;

/** The old tag wherever it stands as a whole tag: "XC1", "XC1:L2.1", "-XC1", inside a key ("QB1:2|XC1:L1"); never inside a longer one. */
const BLOCK = /(?<![\w])XC1(?![\w])/g;
const block = (v: any): any =>
  typeof v === "string" ? v.replace(BLOCK, CP002_BLOCK_RENAME.to)
  : Array.isArray(v) ? v.map(block)
  : v && typeof v === "object" ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k.replace(BLOCK, CP002_BLOCK_RENAME.to), block(x)]))
  : v;
const oneBlock = <A extends { model?: any; ui?: any }>(s: A): A => {
  const asText = typeof s.model === "string", model = block(asText ? JSON.parse(s.model) : s.model ?? null);
  return { ...s, model: asText ? JSON.stringify(model) : model, ui: block(s.ui ?? {}) };
};
/** A saved CP-002 project with the distribution block's new tag (format 17): its own work and each earlier attempt it keeps. */
export function renameCp002Block<T extends { model?: any; ui?: any; attempts?: any[] }>(s: T): T {
  const out = oneBlock(s);
  return Array.isArray(s.attempts) ? { ...out, attempts: s.attempts.map(a => oneBlock(a)) } : out;
}
